import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register, checkDuplicate } from '../../lib/api/user-api';
import { redirectToNaverAuthorize } from '../../lib/naver-auth';
import './SignupPage.css';

function SignupPage() {
  const navigate = useNavigate();
  // 가입 방법 선택: null(선택 전) | 'normal'(일반 회원가입 폼)
  const [method, setMethod] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    password: '',
    passwordConfirm: '',
    email: '',
    phone: '',
    userName: '',
    nickname: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [userIdChecked, setUserIdChecked] = useState(false);
  const [userIdCheckLoading, setUserIdCheckLoading] = useState(false);
  // 이메일 중복 시 계정 연동 모달 (null=닫힘)
  const [linkModal, setLinkModal] = useState(null);

  // 회원가입 응답이 '이미 가입된 이메일(연동 필요)'이면 연동 모달을 띄우고 true 반환.
  // 성공응답(success=false)과 400 에러(err.data) 양쪽에서 동일 로직으로 처리.
  const handleLinkRequired = (resp) => {
    const link = resp?.data;
    if (!link?.linkRequired) return false;
    // 가입폼의 아이디/비밀번호/이메일 보관 → 네이버 로그인(소유 검증) 후 연동에 사용
    sessionStorage.setItem('pending_link', JSON.stringify({
      userId: formData.userId,
      password: formData.password,
      email: formData.email,
    }));
    setLinkModal({
      provider: link.existingProvider || null,
      hasPassword: !!link.existingHasPassword,
      existingUserId: link.existingUserId || '',
      message: resp.message || '',
    });
    return true;
  };

  const closeLinkModal = () => {
    sessionStorage.removeItem('pending_link');
    setLinkModal(null);
  };

  // 네이버 로그인으로 소유 검증 → /sso/callback 에서 pending_link 를 실제 연동한다.
  const handleLinkViaNaver = () => {
    try {
      redirectToNaverAuthorize();
    } catch (e) {
      setErrors({ general: e.message || '네이버 로그인을 시작할 수 없습니다.' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'userId') {
      setUserIdChecked(false);
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleCheckUserId = async () => {
    if (!formData.userId) {
      setErrors({ ...errors, userId: '아이디를 입력하세요' });
      return;
    }

    setUserIdCheckLoading(true);

    try {
      const response = await checkDuplicate(formData.userId, null);

      if (response.success) {
        if (response.data.isUserIdDuplicate) {
          setErrors({ ...errors, userId: '이미 사용 중인 아이디입니다.' });
          setUserIdChecked(false);
        } else {
          setErrors({ ...errors, userId: '' });
          setUserIdChecked(true);
          alert('사용 가능한 아이디입니다.');
        }
      }
    } catch (err) {
      console.error('중복 체크 에러:', err);
      setErrors({ ...errors, userId: '중복 체크 중 오류가 발생했습니다.' });
      setUserIdChecked(false);
    } finally {
      setUserIdCheckLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userId) newErrors.userId = '아이디를 입력하세요';
    if (!userIdChecked) newErrors.userId = '아이디 중복 체크를 해주세요';
    if (!formData.password) newErrors.password = '비밀번호를 입력하세요';
    if (formData.password.length < 6) newErrors.password = '비밀번호는 6자 이상이어야 합니다';
    if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다';
    }
    if (!formData.email) newErrors.email = '이메일을 입력하세요';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = '올바른 이메일 형식이 아닙니다';
    if (!formData.userName) newErrors.userName = '이름을 입력하세요';
    if (!formData.nickname) newErrors.nickname = '닉네임을 입력하세요';

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await register({
        userId: formData.userId,
        email: formData.email,
        password: formData.password,
        userName: formData.userName,
        nickname: formData.nickname,
        phone: formData.phone || null,
      });

      if (response.success) {
        alert('회원가입이 완료되었습니다! 관리자 승인 후 로그인할 수 있습니다.');
        navigate('/login');
      } else if (!handleLinkRequired(response)) {
        setErrors({ general: response.message || '회원가입에 실패했습니다.' });
      }
    } catch (err) {
      console.error('회원가입 에러:', err);
      // 이메일 중복(연동 필요)은 400으로 내려오며 err.data 에 응답 body 가 담겨 온다.
      if (!handleLinkRequired(err?.data)) {
        setErrors({ general: err.message || '회원가입 중 오류가 발생했습니다.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/login');
  };

  // 네이버로 회원가입: 로그인과 동일한 OAuth 흐름(신규면 자동 가입)
  const handleNaverSignup = () => {
    try {
      redirectToNaverAuthorize();
    } catch (e) {
      setErrors({ general: e.message || '네이버 회원가입을 시작할 수 없습니다.' });
    }
  };

  // 가입 방법 선택 화면 (네이버 회원가입 / 일반 회원가입)
  if (!method) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <div className="signup-header">
            <h1>회원가입</h1>
            <p>가입 방법을 선택해주세요</p>
          </div>

          {errors.general && <div className="error-message general-error">{errors.general}</div>}

          <div className="signup-method-select">
            <button
              type="button"
              className="signup-naver-btn"
              onClick={handleNaverSignup}
            >
              <span className="signup-naver-logo" aria-hidden="true">N</span>
              네이버로 회원가입
            </button>

            <button
              type="button"
              className="signup-method-normal"
              onClick={() => setMethod('normal')}
            >
              일반 회원가입
            </button>

            <button type="button" className="signup-method-back" onClick={handleBackClick}>
              뒤로가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <h1>회원가입</h1>
          <p>APT Ranking 서비스를 이용하기 위해 회원가입을 해주세요</p>
        </div>

        <form className="signup-form" onSubmit={handleSubmit}>
          {errors.general && <div className="error-message general-error">{errors.general}</div>}

          <div className="form-group">
            <label htmlFor="userName">이름 *</label>
            <input
              type="text"
              id="userName"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              placeholder="이름을 입력하세요"
              disabled={loading}
            />
            {errors.userName && <span className="error-message">{errors.userName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="nickname">닉네임 *</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              placeholder="닉네임을 입력하세요"
              disabled={loading}
            />
            {errors.nickname && <span className="error-message">{errors.nickname}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="userId">아이디 *</label>
            <div className="input-with-button">
              <input
                type="text"
                id="userId"
                name="userId"
                value={formData.userId}
                onChange={handleChange}
                placeholder="아이디를 입력하세요"
                disabled={loading}
              />
              <button
                type="button"
                className={`check-button ${userIdChecked ? 'checked' : ''}`}
                onClick={handleCheckUserId}
                disabled={loading || userIdCheckLoading || !formData.userId}
              >
                {userIdCheckLoading ? '확인중...' : userIdChecked ? '확인완료' : '중복확인'}
              </button>
            </div>
            {errors.userId && <span className="error-message">{errors.userId}</span>}
            {userIdChecked && !errors.userId && (
              <span className="success-message">사용 가능한 아이디입니다.</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">비밀번호 *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="비밀번호를 입력하세요 (6자 이상)"
              disabled={loading}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="passwordConfirm">비밀번호 확인 *</label>
            <input
              type="password"
              id="passwordConfirm"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              placeholder="비밀번호를 다시 입력하세요"
              disabled={loading}
            />
            {errors.passwordConfirm && <span className="error-message">{errors.passwordConfirm}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">이메일 *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@email.com"
              disabled={loading}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">휴대폰번호</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="010-0000-0000"
              disabled={loading}
            />
          </div>

          <div className="button-group">
            <button type="button" onClick={() => setMethod(null)} className="back-button" disabled={loading}>
              뒤로가기
            </button>
            <button type="submit" className="signup-button" disabled={loading}>
              {loading ? '가입 중...' : '회원가입'}
            </button>
          </div>
        </form>

        {linkModal && (
          <div className="signup-link-overlay" onClick={closeLinkModal}>
            <div className="signup-link-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="signup-link-title">이미 가입된 이메일이에요</h3>
              <p className="signup-link-msg">
                {linkModal.message || '이 이메일로 가입된 계정이 이미 있습니다.'}
              </p>
              {linkModal.provider === 'NAVER' && !linkModal.hasPassword ? (
                <>
                  <p className="signup-link-desc">
                    네이버로 로그인하면 방금 입력한 아이디/비밀번호가 해당 계정에 연동되어,
                    이메일·네이버 두 방식 모두 로그인할 수 있어요.
                  </p>
                  <button type="button" className="signup-naver-btn" onClick={handleLinkViaNaver}>
                    <span className="signup-naver-logo" aria-hidden="true">N</span>
                    네이버로 로그인하여 연동
                  </button>
                </>
              ) : (
                <>
                  <p className="signup-link-desc">
                    이미 이메일/비밀번호로 가입된 계정입니다. 로그인해 주세요.
                  </p>
                  <button
                    type="button"
                    className="signup-button"
                    onClick={() => { sessionStorage.removeItem('pending_link'); navigate('/login'); }}
                  >
                    로그인하러 가기
                  </button>
                </>
              )}
              <button type="button" className="signup-link-cancel" onClick={closeLinkModal}>
                취소
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignupPage;
